import * as styles from "./BreedFilters.module.scss";

import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Button, Form, ListWithCount } from "../../../components/common";

import Fuse from "fuse.js";
import { Arrow, Chevron } from "../../../components/common/Icons";
import { useDogSearchApi } from "../../../context";
import { BreedInput } from "../BreedInput/BreedInput";

export const BreedFilters: React.FC = () => {
	const {
		breedList,
		breedSearchTerm,
		useDogSearchQuery,
		onConfirmSelectedBreeds,
		onCancelSelectedBreeds,
	} = useDogSearchApi();

	// biome-ignore lint/correctness/useExhaustiveDependencies: We only want this to run once
	useEffect(() => {
		useDogSearchQuery();
	}, []);

	const [breedsClassName, setBreedsClassName] = useState(
		styles.selectedtitlewrappernone,
	);

	const [filteredBreedList, setFilteredBreedList] = useState(breedList);
	const [showSelectedBreeds, setShowSelectedBreeds] = useState(false);
	const ArrowIcon = useMemo(
		() => () => (
			<div className={styles.btniconcontainer}>
				<Arrow height={"10px"} width={"16px"} withBorder={false} />
			</div>
		),
		[],
	);

	const updateBreedList = useCallback(() => {
		const fuse = new Fuse(breedList, {
			keys: ["title"],
			threshold: 0.4,
		});
		if (breedSearchTerm)
			setFilteredBreedList(
				fuse.search(breedSearchTerm).map((breed) => breed.item),
			);
		else setFilteredBreedList(breedList);
	}, [breedList, breedSearchTerm]);

	const selectedBreeds = useMemo(
		() => breedList.filter((breed) => breed.selected),
		[breedList],
	);

	const getListHeight = () => {
		const offset = breedsClassName === styles.selectedtitlewrappernone ? 0 : 38;
		if (!showSelectedBreeds) return offset;
		return offset + selectedBreeds.slice(0, 5).length * 48;
	};

	const getSelectedListHeight = () => {
		if (showSelectedBreeds) return selectedBreeds.slice(0, 5).length * 48;
		return 0;
	};

	useEffect(() => {
		updateBreedList();
	}, [updateBreedList]);

	useEffect(() => {
		if (selectedBreeds.length > 0) {
			if (breedsClassName === styles.selectedtitlewrappernone) {
				setBreedsClassName(styles.selectedtitlewrapperinitial);
				setTimeout(
					() => setBreedsClassName(styles.selectedtitlewrapperflash),
					600,
				);
			}
		}
	}, [selectedBreeds, breedsClassName]);

	return (
		<div className={styles.breedfilterscontainer}>
			<div className={styles.searchcontainer}>
				<Form
					className={styles.formwrapper}
					onSubmitFn={() => {}}
					validationSchema={{}}
				>
					<div className={styles.title}>Search breeds</div>
					<BreedInput
						className={styles.searchinput}
						placeholder={"Search Breeds"}
						name={"breed-search"}
					/>
				</Form>
			</div>
			<div className={styles.listscontainer}>
				<div
					style={{
						maxHeight: `calc(100% - ${getListHeight()}px)`,
						overflowY: "scroll",
					}}
				>
					<div className={styles.titlewrapper}>
						<span
							className={styles.listtitle}
						>{`Available Breeds (${breedList.length})`}</span>
					</div>
					<div className={styles.listwrapper}>
						<ListWithCount
							disabled={selectedBreeds.length === 15}
							items={filteredBreedList.sort((a, b) =>
								a.title.localeCompare(b.title),
							)}
						/>
					</div>
				</div>
				<div
					style={{
						overflowY: "hidden",
					}}
				>
					<button	type="button"
						className={breedsClassName}
						key={selectedBreeds.length}
						onClick={() => setShowSelectedBreeds((prev) => !prev)}
					>
						<span className={styles.listtitle}>
							{"Selected Breeds"}
							<div
								key={selectedBreeds.length}
								className={styles.count}
							>{`\u00A0(${selectedBreeds.length}/15)`}</div>
						</span>
						<Chevron
							rotation={showSelectedBreeds ? 180 : 0}
							className={styles.icon}
							height={"20px"}
							width={"20px"}
						/>
					</button>
					<div
						className={styles.listwrapper}
						style={{ height: getSelectedListHeight() }}
					>
						<ListWithCount
							items={selectedBreeds.sort((a, b) =>
								a.title.localeCompare(b.title),
							)}
						/>
					</div>
				</div>
			</div>
			<div className={styles.controls}>
				<div className={styles.backcontainer}>
					<Button
						variant="tertiary"
						fullWidth={true}
						size="small"
						onClick={onCancelSelectedBreeds}
						onKeyDown={(e) => e.key === "Enter" && onCancelSelectedBreeds()}
						ariaLabel="cancel breed selection"
						IconLeft={ArrowIcon}
					>
						Back
					</Button>
				</div>
				<div className={styles.confirmcontainer}>
					<Button
						size="small"
						fullWidth={true}
						onClick={onConfirmSelectedBreeds}
						onKeyDown={(e) => e.key === "Enter" && onConfirmSelectedBreeds()}
						ariaLabel="confirm breeds"
					>{`Add ${breedList.filter((x) => x.selected).length} breeds`}</Button>
				</div>
			</div>
		</div>
	);
};

export default BreedFilters;
